const DEFAULT_LOCATION: LocationInfo = { CountryCode: "US", CurrencyCode: "USD", RegionId: 1, HomeRegionId: 1 };

/**
 * Initialize state sets all application state at once based on api/identity/info.
 * It should be run EITHER in the top scope of app.vue on the server only,
 * OR after a successful login in the client only, but not both.
 */
export default function useInitializeState() {
    const { $api } = useNuxtApp();
    const { locale } = useI18n();
    const isAuthenticated = useStateIsAuthenticated();
    const userInfo = useStateUserInfo();
    const locationInfo = useStateLocationInfo();
    const regionInfo = useStateRegionInfo();
    const currencyInfo = useStateCurrencyInfo();
    const globalConfig = useStateGlobalConfig();
    const config = useRuntimeConfig();
    const { cdnUrl } = config.public;

    // ASP.NET Core makes use of 2 tokens for XSRF prevention
    // the persistent cookie token "eciarvt" and the request token "XSRF-TOKEN"
    const eciarvtCookie = useCookie("eciarvt");
    const xsrfCookie = useCookie("XSRF-TOKEN");
    const xsrfToken = useState("xsrfToken", () => xsrfCookie);

    return async function initializeState() {
        const accountData = await $api<AccountData>("identity/info", { query: { lang: locale.value } });

        if (accountData.XSRFCookieToken) {
            eciarvtCookie.value = accountData.XSRFCookieToken;
        }

        xsrfToken.value = accountData.XSRFRequestToken;
        isAuthenticated.value = accountData.IsAuthenticated ?? false;
        userInfo.value = accountData.User ?? { Roles: [] };
        locationInfo.value = accountData.Location ?? DEFAULT_LOCATION;
        globalConfig.value = {
            isAuthenticated: isAuthenticated.value,
            CDNUrl: cdnUrl,
            CurrencyCode: locationInfo.value.CurrencyCode || "USD",
            LanguageCode: locale.value,
            Features: accountData.Features ?? EMPTY_FEATURE_FLAGS,
            SearchText: "",
        };
        regionInfo.value = accountData.Regions || [];
        currencyInfo.value = accountData.Currencies || [];

        return { isAuthenticated, userInfo, locationInfo, regionInfo, currencyInfo, globalConfig, accountData };
    };
}
